import { useUserRowDetails } from 'hooks/useUserRowDetails';
import { useAuthContext } from 'hooks/useAuthContext';
import { Chip, CircularProgress, SelectChangeEvent, Stack, Typography, useTheme, styled } from '@mui/material';
import { LessonFlat } from 'models/LessonFlat';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRoles } from 'constants/userRoles';
import { PatronType } from 'api/models/request/IMakeUserPatronRequest';
import React from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import RoleSelector from 'components/User/RoleSelector/RoleSelector';
import { createColorFilteredComponent, createPrefixedPropsFilteredComponent } from 'helpers/styledHelpers';

const StyledParticipantChip = createPrefixedPropsFilteredComponent<{ $isParticipant: boolean }>(Chip)(({ $isParticipant }) => ({
  textDecoration: $isParticipant ? 'line-through' : ''
}));

const StyledCancelIcon = createColorFilteredComponent<{ $color: string }>(CancelIcon)(({ $color }) => ({
  color: $color
}));

const StyledLoadingStack = createPrefixedPropsFilteredComponent<{ $padding: string }>(Stack)(({ $padding }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: $padding
}));

const StyledMainContentStack = createPrefixedPropsFilteredComponent<{ $paddingX: string; $paddingY: string }>(Stack)(({ $paddingX, $paddingY }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  paddingLeft: $paddingX,
  paddingRight: $paddingX,
  paddingTop: $paddingY,
  paddingBottom: $paddingY
}));

const StyledLessonsStack = createPrefixedPropsFilteredComponent<{ $paddingX: string }>(Stack)(({ $paddingX }) => ({
  paddingLeft: $paddingX,
  paddingRight: $paddingX
}));

const StyledRoleSelectorStack = styled(Stack)({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row'
});

const StyledParticipantsStack = createPrefixedPropsFilteredComponent<{ $paddingX: string }>(Stack)(({ $paddingX }) => ({
  paddingLeft: $paddingX,
  paddingRight: $paddingX
}));

interface UserProfileFlat {
    id: string;
    displayName: string;
}

export interface UserRowDataDetails {
  assignedLessons: LessonFlat[];
  assignedParticipants: UserProfileFlat[];
  role: string;
}
export const UserRowDetails = React.memo((props: {
  userId: string,
  onUserRoleChanged?: (userId: string, newRole: UserRoles) => void;
  userLessonsChangedAt?: Date;
}) => {
  const [componentState, setComponentState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [data, setData] = useState<UserRowDataDetails | null>(null);
  const {
    fetchUserRowDetails,
    disconnectUserWithLesson,
    disconnectUserWithPatron,
    makeUserParticipant,
    makeUserPatron,
} = useUserRowDetails();
    const theme = useTheme();
    const { t } = useTranslation();
    const { userState } = useAuthContext();

  const fetchData = useCallback(async () => {
    setComponentState('loading');
    const resp = await fetchUserRowDetails(props.userId);
    if (resp?.success && resp.data) {
      setData(resp.data);
      setComponentState('loaded');
    } else {
      setComponentState('error');
    }      
  }, [fetchUserRowDetails, props.userId]);
  
  useEffect(() => {
    if (props.userLessonsChangedAt) {      
      fetchData();
    }
  }, [props.userLessonsChangedAt, fetchData]);

  useEffect(() => {
    if (props.userId && !data) {      
      fetchData();
    }
  }, []);

  const handleDisconnectLesson = async (lessonId: string) => {
    const resp = await disconnectUserWithLesson({ userId: props.userId, lessonId });
    if (resp?.success) {
      setData((prevData) => {
        if (prevData) {
          return {
            ...prevData,
            assignedLessons: prevData.assignedLessons.filter((lesson) => lesson.id !== lessonId),
          };
        }
        return prevData;
      });
    }
  };

  const handleDisconnectUser = async  (userId: string) => {
    const resp = await disconnectUserWithPatron({ userId, patronId: props.userId });
    if (resp?.success) {
      setData((prevData) => {
        if (prevData) {
          return {
            ...prevData,
            assignedParticipants: prevData.assignedParticipants.filter((user) => user.id !== userId),
          };
        }
        return prevData;
      });
    }    
  }

  const handleChangeRole = async (event: SelectChangeEvent<string>) => {
    if(userState.user.userId === props.userId) {
      return;
    }

    if (event.target.value === UserRoles.Patron || event.target.value === UserRoles.PatronLight) {
      const resp = await makeUserPatron(props.userId, event.target.value === UserRoles.Patron ? PatronType.Patron : PatronType.PatronLight);
      if (resp?.success && props.onUserRoleChanged) {
        props.onUserRoleChanged(props.userId, event.target.value);
      } 
    } else if (event.target.value === UserRoles.Participant) {
        const resp = await makeUserParticipant(props.userId);
        if (resp?.success && props.onUserRoleChanged) {
            props.onUserRoleChanged(props.userId, event.target.value);
        }
    }
  };

  if(componentState === 'loading'){
    return (
      <StyledLoadingStack $padding={theme.spacing(1)}>
        <CircularProgress />
      </StyledLoadingStack>
      );
  }

  if (componentState === 'error' && !data) {
    return (
      <StyledLoadingStack $padding={theme.spacing(1)}>
        <Typography variant='h6' color={'red'}>{t('userRowDetails.errorFetchingData')}</Typography>
      </StyledLoadingStack>
    );
  }

  return data && <StyledMainContentStack spacing={2} $paddingX={theme.spacing(5)} $paddingY={theme.spacing(2)}>
         <Typography fontWeight={'500'} variant='subtitle1'>{t('general.lessons')}:</Typography>
         <StyledLessonsStack spacing={1} direction={'row'} flexWrap={'wrap'} $paddingX={theme.spacing(2)}>
         {data.assignedLessons.map((lesson) => (
             <Chip
             key={lesson.id}
             label={lesson.name}
             onDelete={() => handleDisconnectLesson(lesson.id)}
             variant="outlined"
             />
         ))}
         </StyledLessonsStack>
         <StyledRoleSelectorStack>
           <RoleSelector 
             value= {data.role as UserRoles} 
             onChange={handleChangeRole} 
             disabled={data.role === UserRoles.ADMIN} />                 
         </StyledRoleSelectorStack>
         <Typography fontWeight={'500'} variant='subtitle1'>{t('general.participants')}:</Typography>
         <StyledParticipantsStack spacing={1} direction={'row'} flexWrap={'wrap'} $paddingX={theme.spacing(2)}>
         {data.assignedParticipants.map((user) => (
             <StyledParticipantChip 
               key={user.id}
               label={user.displayName}
               onDelete={() => handleDisconnectUser(user.id)}
               variant="outlined"
               $isParticipant={data.role === UserRoles.Participant}
               deleteIcon={<StyledCancelIcon $color={theme.palette.text.secondary} />}
             />
         ))}
         </StyledParticipantsStack>
         </StyledMainContentStack>;  
});
  